import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './Pagination.css';
import { translate } from '../../utils/translator';

interface PaginationProps {
	totalItems: number;
}

const Pagination: React.FC<PaginationProps> = ({
	totalItems
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useParams()

    const defaultPaginationLimits = [10, 15, 20, 25];

	// Get current limit and offset from URL search params with defaults
	const currentLimit = Number(searchParams.get('limit')) || 10;
	const currentOffset = Number(searchParams.get('offset')) || 0;

	const currentPage = Math.floor(currentOffset / currentLimit) + 1;
	const totalPages = Math.ceil(totalItems / currentLimit);

	const updateUrlParams = (newOffset: number, newLimit: number) => {
		const newSearchParams = new URLSearchParams(searchParams);
		
		if (newOffset > 0) {
			newSearchParams.set('offset', newOffset.toString());
		} else {
			newSearchParams.delete('offset');
		}
		
		if (newLimit !== 10) { // assuming 10 is default
			newSearchParams.set('limit', newLimit.toString());
		} else {
			newSearchParams.delete('limit');
		}
		
		setSearchParams(newSearchParams);
	};

	const goToPage = (page: number) => {
		const newOffset = (page - 1) * currentLimit;
		updateUrlParams(newOffset, currentLimit);
	};

	const goToPreviousPage = () => {
		if (currentPage > 1) {
			goToPage(currentPage - 1);
		}
	};

	const goToNextPage = () => {
		if (currentPage < totalPages) {
			goToPage(currentPage + 1);
		}
	};

	const changeLimitAndResetOffset = (newLimit: number) => {
		updateUrlParams(0, newLimit);
	};

	const getVisiblePages = () => {
		const delta = 2;
		const range = [];
		const rangeWithDots = [];

		for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
			range.push(i);
		}

		if (currentPage - delta > 2) {
			rangeWithDots.push(1, '...');
		} else {
			rangeWithDots.push(1);
		}

		rangeWithDots.push(...range);

		if (currentPage + delta < totalPages - 1) {
			rangeWithDots.push('...', totalPages);
		} else {
			rangeWithDots.push(totalPages);
		}

		return rangeWithDots;
	};

	// Don't render pagination if there are no items or only one page
	if (totalItems === 0 || totalPages <= 1) {
		return null;
	}

	const startItem = currentOffset + 1;
	const endItem = Math.min(currentOffset + currentLimit, totalItems);
    

	return (
		<div className="pagination-wrapper">
			<div className="pagination-info-section">
				<span className="pagination-showing-text">
                    {
                    
                    translate(language, "default", "pagination/showing_items")
                    .replace(":start", startItem.toString())
                    .replace(":end", endItem.toString())
                    .replace(":total", totalItems.toString())
                    
                    }
				</span>
				<select 
					value={currentLimit} 
					onChange={(e) => changeLimitAndResetOffset(Number(e.target.value))}
					className="pagination-limit-select"
				>
					{defaultPaginationLimits.map((limit) => (
						<option key={limit} value={limit}>{limit} {translate(language, "default", "pagination/per_page")}</option>
					))}
				</select>
			</div>
			
			<div className="pagination-navigation">
				{/* Mobile-friendly prev/next with page info */}
				<div className="pagination-mobile-nav">
					<button 
						onClick={goToPreviousPage} 
						disabled={currentPage <= 1}
						className="pagination-nav-btn pagination-prev-btn"
					>
					← {translate(language, "default", "pagination/previous")}
					</button>
					
					<span className="pagination-page-info">
						{
                              translate(language, "default", "pagination/showing_items")
                              .replace(":start", startItem.toString())
                              .replace(":end", endItem.toString())
                              .replace(":total", totalItems.toString())
                        }
					</span>
					
					<button 
						onClick={goToNextPage} 
						disabled={currentPage >= totalPages}
						className="pagination-nav-btn pagination-next-btn"
					>
						{translate(language, "default", "pagination/next")} →
					</button>
				</div>

				{/* Desktop pagination with numbered pages */}
				<div className="pagination-desktop-nav">
					<button 
						onClick={goToPreviousPage} 
						disabled={currentPage <= 1}
						className="pagination-nav-btn"
					>
						← {translate(language, "default", "pagination/previous")}
					</button>
					
					<div className="pagination-pages">
						{getVisiblePages().map((page, index) => (
							page === '...' ? (
								<span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
							) : (
								<button
									key={page}
									onClick={() => goToPage(page as number)}
									className={`pagination-page-btn ${page === currentPage ? 'active' : ''}`}
								>
									{page}
								</button>
							)
						))}
					</div>
					
					<button 
						onClick={goToNextPage} 
						disabled={currentPage >= totalPages}
						className="pagination-nav-btn"
					>
						{translate(language, "default", "pagination/next")} →
					</button>
				</div>
			</div>
		</div>
	);
};

export default Pagination;
